import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Divider, List, SvgIcon, Skeleton } from "@mui/material";
import { CoreButton, CoreConfirmationModal, CoreModalDescription, CorePaper, CoreRadioCardIcon } from "../../core";
import { useFocusElementOnMount, useMobileLayout } from "../../hooks";
import i18nPackage from "../../i18n/i18n";
import { LiveSession } from "../../api/fetch";
import {
	getPotentiallyCancelledInvitations,
	type LmsScheduleInfo,
	type LiveSessionWithInvitations,
	type PendingLiveSession,
	type ProctorGroupInfo,
} from "../live/liveHelper";
import styles from "./LiveSessionStep.module.css";
import AddIcon from "../images/add.svg?react";
import StepDescription, { StepWrapper } from "./CommonStepComponents";
import { EditQuizExpandButton } from "../components/EditQuizButton";
import SessionDetails from "../live/SessionDetails";
import SessionHeader from "../live/SessionHeader";
import RegistrationWindowEditor from "../live/RegistrationWindow";
import GuidelineIcon from "../images/live/guidelines.svg?react";
import { UseEditQuizStateReturn } from "../hooks/state/useEditQuizState";
import { ConditionalWrapper } from "../../wrapper";

function ProctorGuideEditor({
	session,
	onChange,
	verityOrigin,
}: {
	session: PendingLiveSession;
	onChange: (session: PendingLiveSession) => void;
	verityOrigin?: string;
}) {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [isIframeLoading, setIsIframeLoading] = useState(true);

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			const { action, proctorGuide } = event.data;

			if (event.source !== iframeRef.current?.contentWindow) return;

			if (action === "proctorGuideLoaded") {
				setIsIframeLoading(false);
			}

			if (iframeRef.current && action === "editorReady") {
				iframeRef.current.contentWindow?.postMessage(
					{ action: "loadProctorGuide", proctorGuide: session.proctorGuide },
					"*",
				);
			}

			if (action === "updateProctorGuide") {
				onChange({ ...session, proctorGuide: proctorGuide });
			}
		};

		window.addEventListener("message", handleMessage);

		return () => {
			window.removeEventListener("message", handleMessage);
		};
	}, [onChange, iframeRef, session]);

	const handleIframeLoad = useCallback(() => {
		iframeRef.current?.contentWindow?.postMessage({ action: "checkEditorReady" }, "*");
	}, []);

	// This is in an iframe because react quill breaks in chrome extension
	return (
		<div className={styles.guidelineWrapper}>
			{verityOrigin && (
				<iframe
					ref={iframeRef}
					src={`${verityOrigin}/quill-editor/index.html#${session.identifier}`}
					className={`${styles.guidelineIframe} ${isIframeLoading ? styles.loading : ""}`}
					onLoad={handleIframeLoad}
				/>
			)}
			{isIframeLoading && <Skeleton variant="rectangular" className={styles.guidelineSkeleton} />}
		</div>
	);
}

export type SessionEntryProps = Pick<
	LiveSessionStepProps,
	"availableProctors" | "availableProctorGroups" | "lmsSchedule"
> & {
	session: PendingLiveSession;
	existingSession?: LiveSessionWithInvitations;
	expanded: boolean;
	setExpanded: (expanded: boolean) => void;
	onChange: (session: PendingLiveSession) => void;
	onDelete: () => void;
	allowDemoSessions: boolean;
	sessionIndex: number;
	isMobileLayout: boolean;
	verityOrigin?: string;
};

function SessionEntry({
	availableProctors,
	availableProctorGroups,
	session,
	existingSession,
	onChange,
	expanded,
	setExpanded,
	onDelete,
	allowDemoSessions,
	sessionIndex,
	isMobileLayout,
	lmsSchedule,
	verityOrigin,
}: SessionEntryProps) {
	const { ref } = useFocusElementOnMount<HTMLLIElement>(expanded && !isMobileLayout);
	return (
		<li className={styles.sessionEntry} ref={ref}>
			<SessionHeader
				allowDemoSessions={allowDemoSessions}
				expanded={expanded}
				setExpanded={setExpanded}
				onChange={onChange}
				onDelete={onDelete}
				session={session}
				sessionIndex={sessionIndex}
				existingSession={existingSession}
			/>
			{expanded && (
				<div className={styles.expandedSessionEntryWrapper}>
					<div className={styles.sessionEditorContainer}>
						<SessionDetails
							session={session}
							existingSession={existingSession}
							onChange={onChange}
							availableProctorGroups={availableProctorGroups}
							availableProctors={availableProctors}
							lmsSchedule={lmsSchedule}
						/>
						<Divider />
						<RegistrationWindowEditor
							availableProctorGroups={availableProctorGroups}
							session={session}
							existingSession={existingSession}
							onChange={onChange}
						/>
					</div>

					{/*
					 * `ProctorGuideEditor` renders its iframe only when an origin is set but its `<Skeleton>`
					 * unconditionally, so a caller that passes none shows a placeholder that spins forever.
					 * Hiding the section is the honest answer for a surface that cannot host the editor — not a
					 * statement about what can be saved, since Lumina carries `proctorGuide` end to end and its
					 * configure surface passes an origin of its own.
					 */}
					{verityOrigin && (
						<EditQuizExpandButton
							icon={<CoreRadioCardIcon icon={<GuidelineIcon />} enabled />}
							title={i18nPackage.t("editQuizPage.liveSessionStep.proctoringGuidelinesTitle")}
							description={i18nPackage.t("editQuizPage.liveSessionStep.proctoringGuidelinesDesc")}
						>
							<ProctorGuideEditor session={session} onChange={onChange} verityOrigin={verityOrigin} />
						</EditQuizExpandButton>
					)}
				</div>
			)}
		</li>
	);
}

function ProctorGroupDeletionWarningDialog({
	onClose,
	message,
	confirmType,
	onConfirm,
}: {
	onClose: () => void;
	message: ReactNode;
	confirmType: "delete" | "confirm";
	onConfirm: () => void;
}) {
	return (
		<CoreConfirmationModal
			open
			onClose={onClose}
			title={
				confirmType === "delete"
					? i18nPackage.t("editQuizPage.liveSessionStep.proctorModal.deleteTitle")
					: i18nPackage.t("editQuizPage.liveSessionStep.proctorModal.confirmTitle")
			}
			onConfirm={onConfirm}
			confirmationType="checkbox"
			checkboxLabel={i18nPackage.t("editQuizPage.liveSessionStep.proctorModal.checkbox")}
			slotProps={{
				confirmButton: {
					color: confirmType === "delete" ? "cancel" : "primary",
					text: confirmType === "delete" ? i18nPackage.t("editQuizPage.liveSessionStep.delete") : undefined,
				},
			}}
		>
			<CoreModalDescription>{message}</CoreModalDescription>
		</CoreConfirmationModal>
	);
}

function filterEditableSessionIds(currTime: Date, ids: string[], existingSessions: Record<string, LiveSession>) {
	return ids.filter((id) => {
		const existingSession = existingSessions[id];
		return !existingSession || new Date(existingSession.endTime) > currTime;
	});
}

type LiveAction = UseEditQuizStateReturn["live"]["action"];
type LiveState = UseEditQuizStateReturn["live"]["state"];

type LiveSessionStepProps = {
	cancelNextConfirmationDialog: () => void;
	confirmNextConfirmationDialog: () => void;
	allowDemoSessions: boolean;
	lmsSchedule?: LmsScheduleInfo;
	verityOrigin?: string;
} & Pick<LiveAction, "addPendingSession" | "updatePendingSession" | "removePendingSession" | "syncStartedSessions"> &
	Pick<
		LiveState,
		| "existingLiveSessions"
		| "pendingLiveSessionIds"
		| "pendingLiveSessions"
		| "availableProctorGroups"
		| "availableProctors"
		| "liveSessionsNextConfirmationMessage"
	>;

export default function LiveSessionsStep({
	existingLiveSessions,
	pendingLiveSessionIds,
	pendingLiveSessions,
	syncStartedSessions,
	availableProctors,
	availableProctorGroups,
	liveSessionsNextConfirmationMessage,
	cancelNextConfirmationDialog,
	confirmNextConfirmationDialog,
	allowDemoSessions,
	lmsSchedule,
	verityOrigin,
	addPendingSession,
	updatePendingSession,
	removePendingSession,
}: LiveSessionStepProps) {
	const [currTime, setCurrTime] = useState(new Date());
	useEffect(() => {
		const interval = setInterval(() => setCurrTime(new Date()), 30 * 1000);
		return () => clearInterval(interval);
	}, []);

	const [deleteConfirmationDialogInfo, setDeleteConfirmationDialogInfo] = useState<
		| {
				id: string;
				proctorGroup: ProctorGroupInfo;
		  }
		| undefined
	>();

	const editablePendingSessionIds = filterEditableSessionIds(currTime, pendingLiveSessionIds, existingLiveSessions);
	const [expandedSessionIds, setExpandedSessionIds] = useState<string[]>(
		editablePendingSessionIds.length === 0 ? [] : [editablePendingSessionIds[editablePendingSessionIds.length - 1]],
	);
	const isMobileLayout = useMobileLayout();

	const handleAddPendingSession = useCallback(() => {
		const identifier = addPendingSession();
		setExpandedSessionIds([identifier]);
	}, [addPendingSession]);

	const confirmRemovePendingSession = (id: string) => {
		const existingSession = existingLiveSessions[id];
		const cancelledInvitations = existingSession
			? getPotentiallyCancelledInvitations(existingSession, availableProctorGroups)
			: [];
		if (cancelledInvitations.length > 0) {
			const proctorGroup = availableProctorGroups[cancelledInvitations[0].proctorGroupId];
			setDeleteConfirmationDialogInfo({ id, proctorGroup });
		} else {
			removePendingSession(id);
		}
	};

	useEffect(() => {
		const validIds = new Set(filterEditableSessionIds(currTime, pendingLiveSessionIds, existingLiveSessions));
		setExpandedSessionIds((expandedIds) => expandedIds.filter((id) => validIds.has(id)));
	}, [currTime, pendingLiveSessionIds, existingLiveSessions]);

	useEffect(() => {
		syncStartedSessions();
	}, [currTime, syncStartedSessions]);

	useEffect(() => {
		// Add a live session if there are none on mount
		if (pendingLiveSessionIds.length === 0) {
			handleAddPendingSession();
		}
	}, []);

	return (
		<StepWrapper>
			<div className={styles.page}>
				<div className={styles.descriptionRow}>
					<StepDescription>{i18nPackage.t("editQuizPage.liveSessionStep.description")}</StepDescription>
					<ConditionalWrapper
						condition={isMobileLayout}
						whenTrue={(children) => <CorePaper className={styles.addToolbar}>{children}</CorePaper>}
					>
						<CoreButton
							className={styles.addButton}
							startIcon={
								<SvgIcon>
									<AddIcon />
								</SvgIcon>
							}
							onClick={handleAddPendingSession}
						>
							{i18nPackage.t("editQuizPage.liveSessionStep.addSession")}
						</CoreButton>
					</ConditionalWrapper>
				</div>

				<List className={styles.sessionsList}>
					{pendingLiveSessionIds.map((id, idx) => {
						const session = pendingLiveSessions[id];
						return (
							<SessionEntry
								key={session.identifier}
								availableProctors={availableProctors}
								availableProctorGroups={availableProctorGroups}
								session={session}
								existingSession={session.id ? existingLiveSessions[session.id] : undefined}
								onChange={(newSession) => updatePendingSession(session.identifier, newSession)}
								expanded={expandedSessionIds.includes(session.identifier)}
								setExpanded={(expanded) => {
									const sessionId = session.identifier;
									const newExpandedSessions = expandedSessionIds.filter((id) => id !== sessionId);
									setExpandedSessionIds(expanded ? newExpandedSessions.concat([sessionId]) : newExpandedSessions);
								}}
								onDelete={() => confirmRemovePendingSession(session.identifier)}
								allowDemoSessions={allowDemoSessions}
								sessionIndex={idx}
								isMobileLayout={isMobileLayout}
								lmsSchedule={lmsSchedule}
								verityOrigin={verityOrigin}
							/>
						);
					})}
				</List>
				{deleteConfirmationDialogInfo && (
					<ProctorGroupDeletionWarningDialog
						onClose={() => setDeleteConfirmationDialogInfo(undefined)}
						confirmType="delete"
						message={deleteConfirmationDialogInfo.proctorGroup.trackCancellationMessage?.replaceAll(
							"{{sessionName}}",
							existingLiveSessions[deleteConfirmationDialogInfo.id]?.name,
						)}
						onConfirm={() => removePendingSession(deleteConfirmationDialogInfo.id)}
					/>
				)}
				{liveSessionsNextConfirmationMessage && (
					<ProctorGroupDeletionWarningDialog
						onClose={cancelNextConfirmationDialog}
						confirmType="confirm"
						message={liveSessionsNextConfirmationMessage}
						onConfirm={confirmNextConfirmationDialog}
					/>
				)}
			</div>
		</StepWrapper>
	);
}
